import React, { useEffect, useState, useCallback } from "react";
import { Section, Eyebrow, SectionTitle, currency } from "@/components/Primitives";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { MapPin, DollarSign, Truck, Plus, ShieldAlert, Search, Gavel, Users, Percent } from "lucide-react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";

const TRADES = ["All", "General", "Plumbing", "Electrical", "Framing", "HVAC", "Masonry", "Roofing"];
const URGENCY = ["All", "Standard", "High", "Urgent"];
const VEHICLES = ["All", "Cargo Van", "Sprinter", "SUV"];

const leadFee = (b) => (b < 5000 ? 50 : b < 25000 ? 100 : b < 100000 ? 150 : 200);
const commissionRate = (b) => (b >= 100000 ? 15 : b >= 25000 ? 20 : 25);

const Marketplace = () => {
  const { user } = useAuth();
  const [jobs, setJobs] = useState([]);
  const [deliveries, setDeliveries] = useState([]);
  const [trade, setTrade] = useState("All");
  const [urgency, setUrgency] = useState("All");
  const [q, setQ] = useState("");
  const [vehicle, setVehicle] = useState("All");
  const [jobModal, setJobModal] = useState(false);
  const [delModal, setDelModal] = useState(false);
  const [bidModal, setBidModal] = useState(null); // job being bid on
  const [bidForm, setBidForm] = useState({ bid_amount: "", proposal: "" });
  const [jobForm, setJobForm] = useState({ title: "", trade: "General", location: "", budget: "", urgency: "Standard", description: "" });
  const [delForm, setDelForm] = useState({ title: "", pickup: "", dropoff: "", vehicle: "Cargo Van", payout: "", notes: "" });

  const loadJobs = useCallback(() => {
    api.get("/jobs", { params: { trade, urgency, q } }).then((r) => setJobs(r.data)).catch(() => {});
  }, [trade, urgency, q]);
  const loadDel = useCallback(() => {
    api.get("/deliveries", { params: { vehicle } }).then((r) => setDeliveries(r.data)).catch(() => {});
  }, [vehicle]);

  useEffect(() => { loadJobs(); }, [loadJobs]);
  useEffect(() => { loadDel(); }, [loadDel]);

  const requireAuth = () => {
    if (!user) { toast.error("Please log in to continue"); return false; }
    return true;
  };

  const openBid = (job) => {
    if (!requireAuth()) return;
    setBidForm({ bid_amount: job.budget || "", proposal: "" });
    setBidModal(job);
  };

  const submitBid = async () => {
    try {
      const { data } = await api.post(`/jobs/${bidModal.id}/bid`, { bid_amount: Number(bidForm.bid_amount), proposal: bidForm.proposal, origin_url: window.location.origin });
      toast.success("Bid placed! Pay the lead fee to unlock the homeowner's details.");
      setBidModal(null);
      if (data.checkout_url) window.location.href = data.checkout_url;
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not place bid");
    }
  };

  const claimDelivery = async (d) => {
    if (!requireAuth()) return;
    try { await api.post(`/deliveries/${d.id}/claim`); toast.success("Delivery claimed! Check your dashboard."); loadDel(); }
    catch (e) { toast.error(e.response?.data?.detail || "Could not claim"); }
  };

  const createJob = async () => {
    if (!requireAuth()) return;
    try {
      await api.post("/jobs", { ...jobForm, budget: Number(jobForm.budget) });
      toast.success("Job posted! Up to 7 contractors can now bid on your lead.");
      setJobModal(false); setJobForm({ title: "", trade: "General", location: "", budget: "", urgency: "Standard", description: "" });
      loadJobs();
    } catch (e) { toast.error("Could not post job"); }
  };

  const createDelivery = async () => {
    if (!requireAuth()) return;
    try {
      await api.post("/deliveries", { ...delForm, payout: Number(delForm.payout) });
      toast.success("Delivery request posted!");
      setDelModal(false); setDelForm({ title: "", pickup: "", dropoff: "", vehicle: "Cargo Van", payout: "", notes: "" });
      loadDel();
    } catch (e) { toast.error("Could not post delivery"); }
  };

  const isLockedContractor = user?.role === "contractor" && user?.verification_status !== "verified";

  return (
    <div>
      <div className="eti-grid-bg border-b border-white/10 py-14">
        <Section>
          <Eyebrow>The Marketplace</Eyebrow>
          <h1 className="font-display text-4xl sm:text-5xl font-extrabold uppercase tracking-tight text-white leading-[1.05]">Job Leads & Cargo Board</h1>
          <p className="mt-4 text-slate-300 max-w-2xl">Homeowners post jobs as exclusive leads. Up to 7 verified contractors bid — pay a lead fee to unlock, win the job, and Exclusive Trades earns a commission on award.</p>
        </Section>
      </div>

      {isLockedContractor && (
        <Section className="pt-8">
          <div className="eti-card rounded-lg p-5 border-primary/40 flex items-start gap-3" data-testid="verification-warning">
            <ShieldAlert className="h-5 w-5 text-primary shrink-0 mt-0.5" />
            <div>
              <p className="font-semibold text-white">Verification required</p>
              <p className="text-sm text-slate-400">Submit your WSIB & insurance in your <a href="/profile" className="text-primary underline">profile</a> to unlock and bid on job leads.</p>
            </div>
          </div>
        </Section>
      )}

      <Section className="py-10">
        <Tabs defaultValue="jobs">
          <TabsList className="bg-secondary">
            <TabsTrigger value="jobs" className="data-[state=active]:bg-primary data-[state=active]:text-white" data-testid="marketplace-tab-jobs">Job Leads</TabsTrigger>
            <TabsTrigger value="cargo" className="data-[state=active]:bg-primary data-[state=active]:text-white" data-testid="marketplace-tab-cargo">Cargo Delivery Board</TabsTrigger>
          </TabsList>

          <TabsContent value="jobs" className="mt-6">
            <div className="flex flex-col lg:flex-row gap-3 mb-6">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-500" />
                <Input placeholder="Search leads..." value={q} onChange={(e) => setQ(e.target.value)} className="bg-secondary border-white/10 pl-9" data-testid="marketplace-search-input" />
              </div>
              <Select value={trade} onValueChange={setTrade}><SelectTrigger className="bg-secondary border-white/10 w-full lg:w-44" data-testid="marketplace-trade-filter"><SelectValue /></SelectTrigger><SelectContent>{TRADES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent></Select>
              <Select value={urgency} onValueChange={setUrgency}><SelectTrigger className="bg-secondary border-white/10 w-full lg:w-40" data-testid="marketplace-urgency-filter"><SelectValue /></SelectTrigger><SelectContent>{URGENCY.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent></Select>
              <Button onClick={() => setJobModal(true)} className="bg-primary hover:bg-primary/90 text-white font-semibold" data-testid="marketplace-post-job-button"><Plus className="h-4 w-4 mr-1" />Post Job</Button>
            </div>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {jobs.map((j) => {
                const spots = (j.max_bidders || 7) - (j.bidders_count || 0);
                const full = spots <= 0;
                const awarded = !!j.awarded_to;
                return (
                  <div key={j.id} className="eti-card rounded-lg p-5 flex flex-col" data-testid={`job-card-${j.id}`}>
                    <div className="flex items-center justify-between">
                      <span className="eti-label">{j.trade}</span>
                      <Badge variant="outline" className="border-white/15 text-slate-300 text-[10px]">{j.urgency}</Badge>
                    </div>
                    <h4 className="font-display font-bold text-white mt-2 text-lg leading-tight">{j.title}</h4>
                    <p className="text-sm text-slate-400 mt-2 line-clamp-2">{j.description}</p>
                    <div className="flex items-center gap-4 mt-3 text-xs text-slate-400">
                      <span className="flex items-center gap-1"><MapPin className="h-3 w-3" />{j.location}</span>
                      <span className="flex items-center gap-1"><Users className="h-3 w-3" />{j.bidders_count || 0}/{j.max_bidders || 7} bidding</span>
                    </div>
                    <div className="mt-3 flex items-center gap-2 text-[11px] font-mono">
                      <span className="px-2 py-0.5 rounded bg-primary/10 text-primary flex items-center gap-1"><Gavel className="h-3 w-3" />Lead {currency(leadFee(j.budget))}</span>
                      <span className="px-2 py-0.5 rounded bg-white/5 text-slate-300 flex items-center gap-1"><Percent className="h-3 w-3" />{commissionRate(j.budget)}% on award</span>
                    </div>
                    <div className="mt-auto pt-4 flex items-center justify-between border-t border-white/5 mt-4">
                      <span className="font-mono text-primary font-bold flex items-center"><DollarSign className="h-4 w-4" />{currency(j.budget).replace("$", "")}</span>
                      {awarded ? (
                        <Badge className="bg-emerald-500/15 text-emerald-400 border-0">Awarded</Badge>
                      ) : (
                        <Button size="sm" onClick={() => openBid(j)} disabled={isLockedContractor || full} className="bg-primary hover:bg-primary/90 text-white font-semibold" data-testid={`job-card-bid-button-${j.id}`}>
                          {full ? "Lead Full" : "Bid on Lead"}
                        </Button>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </TabsContent>

          <TabsContent value="cargo" className="mt-6">
            <div className="flex flex-col lg:flex-row gap-3 mb-6">
              <Select value={vehicle} onValueChange={setVehicle}><SelectTrigger className="bg-secondary border-white/10 w-full lg:w-48" data-testid="cargo-vehicle-filter"><SelectValue /></SelectTrigger><SelectContent>{VEHICLES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent></Select>
              <Button onClick={() => setDelModal(true)} className="bg-primary hover:bg-primary/90 text-white font-semibold" data-testid="cargo-post-button"><Plus className="h-4 w-4 mr-1" />Request Delivery</Button>
            </div>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {deliveries.map((d) => (
                <div key={d.id} className="eti-card rounded-lg p-5 flex flex-col" data-testid={`delivery-card-${d.id}`}>
                  <div className="flex items-center justify-between">
                    <span className="eti-label flex items-center gap-1"><Truck className="h-3 w-3" />{d.vehicle}</span>
                    <Badge variant="outline" className={`text-[10px] ${d.status === "open" ? "border-primary/40 text-primary" : "border-white/15 text-slate-400"}`}>{d.status}</Badge>
                  </div>
                  <h4 className="font-display font-bold text-white mt-2 leading-tight">{d.title}</h4>
                  <div className="mt-3 space-y-1 text-xs text-slate-400">
                    <p><span className="text-slate-500">Pickup:</span> {d.pickup}</p>
                    <p><span className="text-slate-500">Drop:</span> {d.dropoff}</p>
                  </div>
                  <div className="mt-auto pt-4 flex items-center justify-between border-t border-white/5 mt-4">
                    <span className="font-mono text-primary font-bold flex items-center"><DollarSign className="h-4 w-4" />{d.payout}</span>
                    <Button size="sm" disabled={d.status !== "open"} onClick={() => claimDelivery(d)} className="bg-primary hover:bg-primary/90 text-white font-semibold" data-testid={`delivery-claim-button-${d.id}`}>{d.status === "open" ? "Claim Run" : "Claimed"}</Button>
                  </div>
                </div>
              ))}
            </div>
          </TabsContent>
        </Tabs>
      </Section>

      {/* Bid Modal */}
      <Dialog open={!!bidModal} onOpenChange={(o) => !o && setBidModal(null)}>
        <DialogContent className="bg-card border-white/10" data-testid="bid-modal">
          <DialogHeader><DialogTitle className="font-display uppercase">Bid on Lead</DialogTitle></DialogHeader>
          {bidModal && (
            <div className="space-y-3">
              <div className="eti-card rounded-lg p-4">
                <p className="font-semibold text-white">{bidModal.title}</p>
                <p className="text-xs text-slate-400 mt-1">{bidModal.location} · Budget {currency(bidModal.budget)}</p>
                <div className="flex gap-2 mt-3 text-[11px] font-mono">
                  <span className="px-2 py-0.5 rounded bg-primary/10 text-primary">Lead fee {currency(leadFee(bidModal.budget))}</span>
                  <span className="px-2 py-0.5 rounded bg-white/5 text-slate-300">{commissionRate(bidModal.budget)}% commission if awarded</span>
                  <span className="px-2 py-0.5 rounded bg-white/5 text-slate-300">{(bidModal.max_bidders || 7) - (bidModal.bidders_count || 0)} spots left</span>
                </div>
              </div>
              <div><Label className="text-xs text-slate-300">Your Bid Amount ($)</Label><Input type="number" value={bidForm.bid_amount} onChange={(e) => setBidForm({ ...bidForm, bid_amount: e.target.value })} className="bg-secondary border-white/10 mt-1" data-testid="bid-amount-input" /></div>
              <div><Label className="text-xs text-slate-300">Proposal / Message</Label><Textarea value={bidForm.proposal} onChange={(e) => setBidForm({ ...bidForm, proposal: e.target.value })} placeholder="Why should the homeowner pick you?" className="bg-secondary border-white/10 mt-1" data-testid="bid-proposal-input" /></div>
              <p className="text-xs text-slate-500">You'll pay the <span className="text-primary">{currency(leadFee(bidModal.budget))}</span> lead fee to unlock this job. If awarded, you pay {commissionRate(bidModal.budget)}% of the job value to Exclusive Trades.</p>
            </div>
          )}
          <DialogFooter><Button onClick={submitBid} className="bg-primary hover:bg-primary/90 text-white font-semibold w-full" data-testid="bid-submit-button">Place Bid & Pay Lead Fee</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Post Job Modal */}
      <Dialog open={jobModal} onOpenChange={setJobModal}>
        <DialogContent className="bg-card border-white/10" data-testid="post-job-modal">
          <DialogHeader><DialogTitle className="font-display uppercase">Post a Job Lead</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label className="text-xs text-slate-300">Title</Label><Input value={jobForm.title} onChange={(e) => setJobForm({ ...jobForm, title: e.target.value })} className="bg-secondary border-white/10 mt-1" data-testid="job-title-input" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-xs text-slate-300">Trade</Label><Select value={jobForm.trade} onValueChange={(v) => setJobForm({ ...jobForm, trade: v })}><SelectTrigger className="bg-secondary border-white/10 mt-1"><SelectValue /></SelectTrigger><SelectContent>{TRADES.filter((t) => t !== "All").map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent></Select></div>
              <div><Label className="text-xs text-slate-300">Urgency</Label><Select value={jobForm.urgency} onValueChange={(v) => setJobForm({ ...jobForm, urgency: v })}><SelectTrigger className="bg-secondary border-white/10 mt-1"><SelectValue /></SelectTrigger><SelectContent>{URGENCY.filter((t) => t !== "All").map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent></Select></div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-xs text-slate-300">Location</Label><Input value={jobForm.location} onChange={(e) => setJobForm({ ...jobForm, location: e.target.value })} className="bg-secondary border-white/10 mt-1" data-testid="job-location-input" /></div>
              <div><Label className="text-xs text-slate-300">Budget ($)</Label><Input type="number" value={jobForm.budget} onChange={(e) => setJobForm({ ...jobForm, budget: e.target.value })} className="bg-secondary border-white/10 mt-1" data-testid="job-budget-input" /></div>
            </div>
            <div><Label className="text-xs text-slate-300">Description</Label><Textarea value={jobForm.description} onChange={(e) => setJobForm({ ...jobForm, description: e.target.value })} className="bg-secondary border-white/10 mt-1" data-testid="job-desc-input" /></div>
          </div>
          <DialogFooter><Button onClick={createJob} className="bg-primary hover:bg-primary/90 text-white font-semibold w-full" data-testid="job-submit-button">Post Job Lead</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delivery Modal */}
      <Dialog open={delModal} onOpenChange={setDelModal}>
        <DialogContent className="bg-card border-white/10" data-testid="post-delivery-modal">
          <DialogHeader><DialogTitle className="font-display uppercase">Request a Delivery</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label className="text-xs text-slate-300">What's being delivered</Label><Input value={delForm.title} onChange={(e) => setDelForm({ ...delForm, title: e.target.value })} className="bg-secondary border-white/10 mt-1" data-testid="delivery-title-input" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-xs text-slate-300">Pickup</Label><Input value={delForm.pickup} onChange={(e) => setDelForm({ ...delForm, pickup: e.target.value })} className="bg-secondary border-white/10 mt-1" data-testid="delivery-pickup-input" /></div>
              <div><Label className="text-xs text-slate-300">Drop-off</Label><Input value={delForm.dropoff} onChange={(e) => setDelForm({ ...delForm, dropoff: e.target.value })} className="bg-secondary border-white/10 mt-1" data-testid="delivery-dropoff-input" /></div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-xs text-slate-300">Vehicle</Label><Select value={delForm.vehicle} onValueChange={(v) => setDelForm({ ...delForm, vehicle: v })}><SelectTrigger className="bg-secondary border-white/10 mt-1"><SelectValue /></SelectTrigger><SelectContent>{VEHICLES.filter((t) => t !== "All").map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent></Select></div>
              <div><Label className="text-xs text-slate-300">Payout ($)</Label><Input type="number" value={delForm.payout} onChange={(e) => setDelForm({ ...delForm, payout: e.target.value })} className="bg-secondary border-white/10 mt-1" data-testid="delivery-payout-input" /></div>
            </div>
          </div>
          <DialogFooter><Button onClick={createDelivery} className="bg-primary hover:bg-primary/90 text-white font-semibold w-full" data-testid="delivery-submit-button">Post Request</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Marketplace;
